// @flow
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import type { RouterProps } from 'types/models-config';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import ProgressiveContainer from 'components/container/ProgressiveContainer';
import _ from 'lodash';
import Toastr from 'utils/toastr-util';
import type { UserState } from 'reducers/user';
import DocumentTitle from 'react-document-title';
import { isRequestsPending, hasJustSuccess, isRequestsSuccessful } from 'utils/request-state-utils';
import { DetailsHeader } from 'components/custom';
import ConfirmationModal from 'components/modals/confirmationModal';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Button, Segment, Grid, Dropdown, Divider } from 'semantic-grab-ui/src';
import { editEntity, setActiveEntity, getEntity, deleteEntity, approveEntity } from 'actions/common';
import { setHasDetailsHeader } from 'actions/app';
import { queryStringFromObject } from 'utils/route-utils';
import { hasPermission } from 'utils/user-utils';
import { parseUCDSyncErrMsg } from 'omega-rtc/utils/ucd-utils';
import { scrollToElement } from 'omega-rtc/utils/trident-node-utils';
import scribe from '@grab-x/scribe-web-sdk';
import ScribeEventName from '../../constants/scribe';
import { sendOpenEvent, processCampaignData } from '../../utils/scribe';


type Props = {
  entityID: number,
  entities: any,
  params: Object,
  userPermissions: Object,
  user: UserState,
  permissionKey: string,
  hasDetailsHeader: boolean,
  localeSelector: string,
  allowDuplication?: boolean,
  allowActivation?: boolean,
  allowDeletion?: boolean,
  allowApproval?: boolean,
  hasCountryIDPath?: boolean,
  allowUpdateFunc: Function,
  hasFieldUpdatePermissionFunc: Function,
  hasFieldCreatePermissionFunc: Function,
  nonDuplicateFields?: Array<string>,
  disableOnSuccessLink: Boolean,
  modelName: string,
  webPath: string,
  apiPath: string,
  stateKey: string,
  views: Object,
  idField: string,
  nameField: string,
  entity: Object,
  router: Object,
  routerProps: RouterProps,
  editActionVerb: string,
  setHasDetailsHeader: Function,
  getEntity: Function,
  setActiveEntity: Function,
  editEntity: Function,
  approveEntity: Function,
  deleteEntity: Function,
  getEntityRequest: RequestStates,
  fetchEntityFromList: Boolean,
  setActiveEntityRequest: RequestStates,
  editEntityRequest: RequestStates,
  deleteEntityRequest: RequestStates,
  approveEntityRequest: RequestStates,
  hasDuplicatePath: Function,
  editErrorLocaleKey: string,
  isStayOnCurrentPage: boolean,
  ucdErrMessage: string,
};

class EditPage extends Component {
  props: Props;

  constructor (props: Props) {
    super(props);
    this.state = { openWarning: false };
  }

  static contextTypes = {
    router: PropTypes.object,
  };

  componentDidMount() {
    const { getEntity, setHasDetailsHeader, hasDetailsHeader, modelName, entityID, routerProps, params, user } = this.props;
    getEntity(modelName, entityID, { ..._.get(routerProps, 'location.query', {}), ...params });
    setHasDetailsHeader(!!hasDetailsHeader);
    sendOpenEvent(_.get(user, 'email'), { campaignId: entityID });
  }

  UNSAFE_componentWillUpdate(nextProps: Props) {
    const { routerProps = {}, views = {} } = nextProps;
    let { webPath } = nextProps;
    const { entity, user } = nextProps;
    if (!_.isEmpty(routerProps.routeParams)) {
      _.forEach(routerProps.routeParams, (val, key) => {
        webPath = _.replace(webPath, `:${key}`, val);
      });
    }
    let successPath = '';
    if (views.edit) {
      if (views.show) successPath = `${webPath}/${nextProps.entityID}`;
      else if (this.props.hasCountryIDPath) successPath = `${webPath}/${parseInt(this.props.params.countryID, 10)}/${nextProps.entityID}/edit${location.search || ''}`;
      else successPath = `${webPath}/${nextProps.entityID}/edit${location.search || ''}`;
    }
    if (hasJustSuccess(this.props.editEntityRequest, nextProps.editEntityRequest)) {
      scribe.sendEvent(
        ScribeEventName.CAMPAIGN_EDITED,
        processCampaignData({ ...entity, user }),
      );
      if (nextProps.ucdErrMessage) {
        const reason = parseUCDSyncErrMsg(nextProps.ucdErrMessage);
        Toastr.error(`Trident Campaign updated successfully, but failed to update in Holos : ${reason}`);
      } else if (this.props.disableOnSuccessLink) {
        Toastr.success(
          i18n.t('flash.actions.updated', { model_name: i18n.t(`models.${nextProps.localeSelector}`) })
        );
      } else {
        Toastr.success(
          i18n.t('flash.actions.updated', { model_name: i18n.t(`models.${nextProps.localeSelector}`) }),
          successPath
        );
      }

      if (this.props.isStayOnCurrentPage) {
        nextProps.getEntity(nextProps.modelName, nextProps.entityID, { ..._.get(nextProps, 'location.query', {}), ...nextProps.params });
      } else if (this.props.redirectPath) {
        this.context.router.push(this.props.redirectPath(nextProps.entity));
      } else {
        this.context.router.push(`${webPath}${location.search || ''}`);
      }
    }
    if (hasJustSuccess(this.props.deleteEntityRequest, nextProps.deleteEntityRequest)) {
      this.context.router.push(`${webPath}${location.search || ''}`);
    }
    if (hasJustSuccess(this.props.approveEntityRequest, nextProps.approveEntityRequest)) {
      this.context.router.push(`${webPath}${location.search || ''}`);
    }
  }

  handleEntityChange = (entity: Object) => {
    const newEntity = { ...this.props.entity, ...entity };
    this.props.setActiveEntity(this.props.modelName, newEntity);
  };

  scrollToErrMsg = () => {
    // waiting for 100ms for the UI state to update, otherwise the errMsg div is not rendered in UI yet.
    setTimeout(() => {
      if (!scrollToElement(-140, 'nodeErrMsg')) {
        scrollToElement(-140, 'formErrMsg');
      }
    }, 100);
  };

  handleEdit = () => {
    if (_.isFunction(this.props.allowUpdateFunc) && !this.props.allowUpdateFunc(this.props.entity)) {
      Toastr.error(i18n.t(`${this.props.localeSelector}.errors.${this.props.editErrorLocaleKey}`));
    } else if (!_.isFunction(this.entityForm.isValid) || this.entityForm.isValid()) {
      const { modelName, entityID, entity, editActionVerb } = this.props;
      this.props.editEntity(modelName, entityID, entity, editActionVerb);
    } else {
      Toastr.error(i18n.t('errors.form'));
      this.scrollToErrMsg();
    }
  };

  handleApprove = () => {
    const { modelName, entityID } = this.props;
    this.props.approveEntity(modelName, entityID);
  }

  handleDelete = () => {
    const { modelName, entityID } = this.props;
    this.props.deleteEntity(modelName, entityID);
  };

  onWarningChange = () => {
    const { openWarning } = this.state;
    this.setState({ openWarning: !openWarning });
  };

  cancel = () => {
    // eslint-disable-next-line no-alert
    const confirmValue = confirm(i18n.t('common.leaving_page_confirmation'));
    if (!confirmValue) return;
    this.props.router.goBack();
  };

  render() {
    const { openWarning } = this.state;
    const { views: { entityForm: EntityForm }, entity: stateEntity, webPath, idField, nameField, localeSelector, userPermissions, permissionKey, allowDuplication, allowActivation, allowDeletion, allowApproval, user } = this.props;
    const entity = isRequestsSuccessful(this.props.getEntityRequest) ? stateEntity : null;
    const entityName = _.get(entity, nameField);
    const isLoading: boolean = isRequestsPending(
      this.props.getEntityRequest,
      this.props.editEntityRequest
    );
    const canActivateEntity = allowActivation && hasPermission(userPermissions, permissionKey, '*', 'active');
    const canApproveEntity = allowApproval && hasPermission(userPermissions, permissionKey, '*', 'active');
    let canCreateEntity = allowDuplication && hasPermission(userPermissions, permissionKey, '*', 'create');
    const canDeleteEntity = allowDeletion && hasPermission(userPermissions, permissionKey, '*', 'delete');
    let canUpdateEntity = hasPermission(userPermissions, permissionKey, '*', 'update');
    if (_.isFunction(this.props.hasFieldUpdatePermissionFunc)) {
      canUpdateEntity = canUpdateEntity || this.props.hasFieldUpdatePermissionFunc(entity, userPermissions);
    }
    if (_.isFunction(this.props.hasFieldCreatePermissionFunc)) {
      canCreateEntity = canCreateEntity || this.props.hasFieldCreatePermissionFunc(entity, userPermissions);
    }
    let duplicatePath = `${webPath}/new?${queryStringFromObject({ entityIDToDuplicate: _.get(entity, idField) })}`;
    if (_.isFunction(this.props.hasDuplicatePath)) {
      duplicatePath = this.props.hasDuplicatePath(entity);
    }
    const primaryButton = canApproveEntity && (
      <Button className="entity-create-btn" primary floated="right" onClick={this.handleApprove}>
        {i18n.t('entities.approve')}
      </Button>);
    const dropdownContentJsx = (canCreateEntity || canDeleteEntity) ? (
      <Dropdown.Menu>
        {canCreateEntity && <Dropdown.Item text={i18n.t('entities.duplicate')} as={Link} to={duplicatePath} />}
        {canDeleteEntity && <Dropdown.Item text={i18n.t('entities.delete')} onClick={this.onWarningChange} /> }
      </Dropdown.Menu>
    ) : null;
    return (
      <DocumentTitle title={i18n.t(`${localeSelector}.edit.title`)}>
        <div>
          <ConfirmationModal
            header={i18n.t('common.delete_warning.title', { model: i18n.t(`models${localeSelector}`) })}
            content={i18n.t('common.delete_warning.content', { model: i18n.t(`models${localeSelector}`) })}
            onConfirmation={this.handleDelete}
            onClose={this.onWarningChange}
            open={openWarning}
            yesText={i18n.t('common.delete_warning.yes', { model: i18n.t(`models${localeSelector}`) })}
            noText={i18n.t('common.delete_warning.no', { model: i18n.t(`models${localeSelector}`) })}
            noStyle={'basic'}
            yesStyle={'color: orange'}
          />
          {
            entity &&
            <DetailsHeader
              detailsTitle={entityName}
              primaryButton={primaryButton}
              detailsDropdown={dropdownContentJsx}
            />
          }
          <Divider hidden />
          <ProgressiveContainer isLoading={isLoading}>
            {entity &&
            <div>
              <EntityForm
                setRef={form => (this.entityForm = form)}
                model={entity}
                onModelChange={this.handleEntityChange}
                userPermissions={userPermissions}
                canActivate={canActivateEntity}
                user={user}
                isEdit
              />
              <Segment>
                <Grid columns="equal">
                  <Grid.Column>
                    <Button onClick={this.cancel}>
                      {i18n.t('entities.cancel')}
                    </Button>
                  </Grid.Column>
                  <Grid.Column textAlign="right">
                    <Button
                      className="entity-save-btn"
                      primary
                      onClick={this.handleEdit}
                      disabled={!canUpdateEntity}
                    >
                      {i18n.t('entities.save')}
                    </Button>
                  </Grid.Column>
                </Grid>
              </Segment>
            </div>}
          </ProgressiveContainer>
        </div>
      </DocumentTitle>
    );
  }
}

function mapStateToProps(state: StoreState, ownProps: Object) {
  const {
    modelName,
    stateKey,
    idField,
    nameField,
    allowActivation,
    allowDuplication,
    allowDeletion,
    allowApproval,
    permissionKey,
    hasDetailsHeader,
    webPath,
    apiPath,
    localeSelector,
    editActionVerb,
    views,
    editErrorLocaleKey,
    hasDuplicatePath,
    hasCountryIDPath,
    allowUpdateFunc,
    hasFieldUpdatePermissionFunc,
    hasFieldCreatePermissionFunc,
    params = {},
    location,
    routeParams,
    fetchEntityFromList,
    disableOnSuccessLink,
    isStayOnCurrentPage,
  } = ownProps;
  const entityState = _.get(state, stateKey, {});
  return {
    entityID: params.entityID,
    params,
    modelName,
    stateKey,
    idField,
    nameField,
    allowActivation,
    allowDuplication,
    allowDeletion,
    allowApproval,
    permissionKey,
    hasDetailsHeader,
    webPath,
    apiPath,
    editErrorLocaleKey,
    localeSelector,
    views,
    fetchEntityFromList,
    hasDuplicatePath,
    hasCountryIDPath,
    isStayOnCurrentPage,
    allowUpdateFunc,
    hasFieldUpdatePermissionFunc,
    hasFieldCreatePermissionFunc,
    disableOnSuccessLink,
    editActionVerb,
    entity: entityState.active,
    routerProps: { params, location, routeParams },
    userPermissions: state.app.user.userPermissions,
    user: state.app.user,
    entities: _.get(state, [stateKey, 'entities'], []),
    getEntityRequest: _.get(state.requests, `getEntity${_.upperFirst(stateKey)}Request`, {}),
    editEntityRequest: _.get(state.requests, `editEntity${_.upperFirst(stateKey)}Request`, {}),
    deleteEntityRequest: _.get(state.requests, `deleteEntity${_.upperFirst(stateKey)}Request`, {}),
    approveEntityRequest: _.get(state.requests, `approveEntity${_.upperFirst(stateKey)}Request`, {}),
    ucdErrMessage: _.get(state, 'newTridents.ucdErrMessage', ''),
  };
}

const ConnectedEditPage = connect(mapStateToProps, {
  getEntity,
  setActiveEntity,
  editEntity,
  setHasDetailsHeader,
  deleteEntity,
  approveEntity,
})(EditPage);

// Exporting named connected component so that root index does not need to reference
// the import name to fix circular dependencies
export { ConnectedEditPage as EditPage };
